import React, { useEffect, useMemo, useState } from 'react'
import { Tour, TourProps } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { updateTourProgress } from '../../../store/session/action-creators'
import { APPEALS_TOUR_ENABLED } from '../tours-enabled'

type Props = {
  pathname: string
  menuAppealsTarget: () => HTMLElement
  tableTarget: () => HTMLElement
  filtersTarget: () => HTMLElement
  createButtonTarget: () => HTMLElement
  openSourceAppealCreateDictionary: () => void
  goToCreateAppeal: () => void
}

const TOUR_ID = 'tour.appeals.v1'
const PREREQ_TOUR_ID = 'setup.case_source_appeal_create'

function safeTarget(fn: () => HTMLElement | null | undefined): () => HTMLElement {
  return () => fn() || document.body
}

export function AppealsOnboardingTour({
  pathname,
  menuAppealsTarget,
  tableTarget,
  filtersTarget,
  createButtonTarget,
  openSourceAppealCreateDictionary,
  goToCreateAppeal,
}: Props) {
  const dispatch = useDispatch()
  const sessionData = useSelector((s: any) => s.session?.sessionData)
  const tours = (sessionData as any)?.jsonSettings?.tours || {}
  const progress = tours?.[TOUR_ID] || {}
  const prereqProgress = tours?.[PREREQ_TOUR_ID] || {}

  const isEligible = pathname === '/appeals'
  const completed = Boolean(progress?.completed)
  const closed = progress?.stage === 'closed'
  const prereqCompleted = Boolean(prereqProgress?.completed)
  const mode: 'prereq' | 'main' = prereqCompleted ? 'main' : 'prereq'

  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<number>(Number(progress?.currentStep) || 0)

  // Открываем тур автоматически для нового пользователя (если не completed и не closed)
  useEffect(() => {
    const userId = sessionData?.user?.id
    if (!userId) return
    if (!isEligible) return
    if (completed) return
    if (closed) return

    setOpen(true)
  }, [sessionData?.user?.id, isEligible, completed, closed])

  // Смена режима (prereq -> main): корректно выставляем текущий шаг
  useEffect(() => {
    if (!isEligible || completed) return
    if (mode === 'prereq') {
      setCurrent(0)
      return
    }

    const stepFromStore = Number(progress?.currentStep) || 0
    setCurrent(Number.isNaN(stepFromStore) ? 0 : stepFromStore)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isEligible, completed])

  // Если прогресс подгрузился позже — синхронизируем шаг (и клампим под кол-во шагов)
  useEffect(() => {
    if (mode !== 'main') return
    const stepFromStore = Number(progress?.currentStep) || 0
    if (!Number.isNaN(stepFromStore) && stepFromStore !== current) {
      setCurrent(stepFromStore)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress?.currentStep, mode])

  const steps: TourProps['steps'] = useMemo(() => {
    if (!prereqCompleted) {
      return [
        {
          title: 'Подготовка к работе с обращениями',
          description:
            'Перед тем как создавать обращения вручную, нужно заполнить справочник «Вид контакта (ручной)». Добавьте хотя бы одно значение — и мы продолжим тур.',
          // Здесь лучше без фокуса на UI-элемент — иначе сбивает пользователя.
          // null → Tour рендерится по центру, без подсветки конкретного блока.
          target: () => null,
          placement: 'center',
          nextButtonProps: {
            children: 'Открыть справочник',
            onClick: openSourceAppealCreateDictionary as any,
          },
          prevButtonProps: { style: { display: 'none' } },
        },
      ]
    }

    return [
      {
        title: 'Обращения',
        description:
          'Это раздел, где хранится весь поток входящих обращений: заявки с сайта, звонки, сообщения и другие каналы. Здесь удобно быстро увидеть новые обращения, понять их статус и взять в работу.',
        target: safeTarget(menuAppealsTarget),
      },
      {
        title: 'Список обращений',
        description:
          'В таблице — все обращения, доступные вам по правам. Они попадают сюда автоматически из интеграций и каналов связи (например, сайт/телефония/формы), а также могут быть созданы вручную.',
        target: safeTarget(tableTarget),
      },
      {
        title: 'Как работать с таблицей',
        description:
          'Таблица поддерживает сортировку по столбцам, постраничный просмотр и быстрый доступ к действиям. Чтобы открыть карточку обращения и посмотреть детали — просто кликните по строке.',
        target: safeTarget(tableTarget),
      },
      {
        title: 'Фильтры',
        description:
          'Фильтры помогают быстро найти нужные обращения: по стадии, номеру и другим параметрам. Используйте их, чтобы “сужать” список и не просматривать всё вручную.',
        target: safeTarget(filtersTarget),
      },
      {
        title: 'Ручное создание обращения',
        description:
          'Если обращение пришло по нестандартному каналу, вы можете оформить его вручную. Нажмите кнопку и заполните основные данные — обращение появится в списке и будет доступно для обработки.',
        target: safeTarget(createButtonTarget),
        nextButtonProps: {
          children: 'Перейти к созданию',
          onClick: goToCreateAppeal as any,
        },
      },
    ]
  }, [
    prereqCompleted,
    menuAppealsTarget,
    tableTarget,
    filtersTarget,
    createButtonTarget,
    openSourceAppealCreateDictionary,
    goToCreateAppeal,
  ])

  const persistStep = async (step: number) => {
    if (mode !== 'main') return
    await dispatch(
      updateTourProgress(TOUR_ID, {
        currentStep: step,
        stage: 'in_progress',
      }) as any,
    )
  }

  const handleClose = async () => {
    setOpen(false)
    await dispatch(updateTourProgress(TOUR_ID, { currentStep: current, stage: 'closed' }) as any)
  }

  const handleFinish = async () => {
    if (mode === 'prereq') {
      setOpen(false)
      await dispatch(updateTourProgress(TOUR_ID, { currentStep: 0, stage: 'closed' }) as any)
      return
    }

    setOpen(false)
    await dispatch(
      updateTourProgress(TOUR_ID, {
        currentStep: steps.length - 1,
        stage: 'completed',
        completed: true,
      }) as any,
    )
  }

  const handleChange = async (nextCurrent: number) => {
    // В prereq режиме только 1 шаг (0)
    const clamped = Math.max(0, Math.min(nextCurrent, steps.length - 1))
    setCurrent(clamped)
    await persistStep(clamped)
  }

  if (!APPEALS_TOUR_ENABLED || !isEligible || completed) return null

  return (
    <Tour
      open={open}
      current={current}
      onClose={handleClose}
      onFinish={handleFinish}
      onChange={handleChange}
      closeIcon={undefined}
      steps={steps}
    />
  )
}
